/**
 * Detecção do aparelho para o convite de instalação do app (PWA).
 *
 * Tudo aqui é função pura sobre o que o navegador informa, para que o cartão
 * automático, a folha de passos, o bloco da confirmação e o item fixo usem a
 * mesma regra. O `lib/brandGroupGuard.ts` mantém a própria cópia de
 * `isStandaloneMode` de propósito (fora do escopo desta feature).
 */

export type InstallPlatform = "ios" | "android" | "desktop";

export type InAppBrowserInfo = {
  inApp: boolean;
  app: string | null;
};

export type InstallPlatformInput = {
  userAgent: string;
  platform?: string | null;
  maxTouchPoints?: number | null;
};

/**
 * iPhone, iPod e iPad pelo user agent são iOS. O iPad com iPadOS 13 ou mais
 * novo se apresenta como Mac (`MacIntel`), e só o toque o diferencia de um
 * computador: por isso `maxTouchPoints > 1` também conta como iOS (INST-03).
 */
export function detectInstallPlatform({
  userAgent,
  platform,
  maxTouchPoints,
}: InstallPlatformInput): InstallPlatform {
  const ua = userAgent || "";

  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  // Android antes da regra do iPad: a emulação de Android no navegador do
  // computador mantém `platform` = `MacIntel` com toque (QA de 06/10/2026).
  if (/Android/i.test(ua)) return "android";
  if (platform === "MacIntel" && (maxTouchPoints ?? 0) > 1) return "ios";

  return "desktop";
}

/**
 * Navegadores internos de apps, em que normalmente não dá para instalar.
 * O WhatsApp fica fora até a validação em aparelho mostrar que ele abre
 * navegador próprio (decisão registrada na spec).
 */
const IN_APP_BROWSERS: { app: string; pattern: RegExp }[] = [
  { app: "Instagram", pattern: /Instagram/ },
  { app: "Facebook", pattern: /FBAN|FBAV|FB_IAB/ },
  { app: "TikTok", pattern: /musical_ly|BytedanceWebview|TikTok/ },
  { app: "Line", pattern: /Line\// },
  { app: "Snapchat", pattern: /Snapchat/ },
];

export function detectInAppBrowser(userAgent: string): InAppBrowserInfo {
  const ua = userAgent || "";
  const match = IN_APP_BROWSERS.find(({ pattern }) => pattern.test(ua));

  return match ? { inApp: true, app: match.app } : { inApp: false, app: null };
}

function matchesDisplayMode(mode: string) {
  return typeof window.matchMedia === "function"
    ? window.matchMedia(`(display-mode: ${mode})`).matches
    : false;
}

/** O app já roda instalado (INST-07). */
export function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined") return false;

  return (
    matchesDisplayMode("standalone") ||
    matchesDisplayMode("window-controls-overlay") ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}
